import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import {
  Container,
  Paper,
  Typography,
  CircularProgress,
  Box,
  Alert,
  Button,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

export const EmailConfirmationCallback: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [organizationData, setOrganizationData] = useState<any>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const handleEmailConfirmation = async () => {
      // 既に処理中の場合はスキップ
      if (isProcessing) {
        return;
      }
      setIsProcessing(true);
      try {
        // URLハッシュからトークンを取得して処理
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const type = hashParams.get('type');
        
        // メール確認の場合、セッションを設定
        if (accessToken && refreshToken && type === 'signup') {
          const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          
          if (sessionError) {
            throw new Error(`セッション設定エラー: ${sessionError.message}`);
          }
        }
        
        // ユーザー情報を取得
        const { data: { user }, error } = await supabase.auth.getUser();

        if (error || !user) {
          throw new Error('メール確認に失敗しました。');
        }

        // ユーザーがメール確認済みかチェック
        if (user.confirmed_at) {
          // 組織情報を確認（既に作成されているはず）
          const { data: userOrgs } = await supabase
            .from('user_organizations')
            .select('organization_id, organizations(name)')
            .eq('user_id', user.id)
            .single();
          
          if (userOrgs?.organizations) {
            setOrganizationData(userOrgs.organizations);
          }

          setStatus('success');
          
          // 3秒後にダッシュボードへ遷移
          setTimeout(() => {
            navigate('/dashboard');
          }, 3000);
        } else {
          throw new Error('メール確認が完了していません。');
        }
      } catch (error: any) {
        setErrorMessage(error.message || 'メール確認中にエラーが発生しました。');
        setStatus('error');
      }
    };

    handleEmailConfirmation();
  }, [navigate]);

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        {status === 'loading' && (
          <>
            <CircularProgress size={60} sx={{ mb: 3 }} />
            <Typography variant="h6">
              メールアドレスを確認しています...
            </Typography>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mb: 3 }} />
            <Typography variant="h5" gutterBottom>
              メール確認が完了しました！
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {organizationData && (
                <>
                  マンション管理組合「{organizationData.name}」の登録が完了しました。
                  <br />
                </>
              )}
              まもなくダッシュボードへ移動します...
            </Typography>
          </>
        )}

        {status === 'error' && (
          <>
            <ErrorIcon sx={{ fontSize: 60, color: 'error.main', mb: 3 }} />
            <Typography variant="h5" gutterBottom>
              エラーが発生しました
            </Typography>
            <Alert severity="error" sx={{ mb: 3 }}>
              {errorMessage}
            </Alert>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant="contained"
                onClick={() => navigate('/login')}
                fullWidth
              >
                ログイン画面へ
              </Button>
              <Button
                variant="text"
                onClick={() => navigate('/signup')}
                fullWidth
              >
                新規登録をやり直す
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Container>
  );
};